import { textItemId } from './utils.js'

function occurrenceCount(text, query) {
  let count = 0
  let offset = 0
  while (offset <= text.length - query.length) {
    const index = text.indexOf(query, offset)
    if (index < 0) break
    count += 1
    offset = index + Math.max(1, query.length)
  }
  return count
}

export const searchMethods = {
  openSearch() {
    if (!this.pdf) return
    this.searchBar.hidden = false
    requestAnimationFrame(() => {
      this.searchInput.focus()
      this.searchInput.select()
    })
  },

  closeSearch() {
    this.searchBar.hidden = true
    this.searchInput.value = ''
    this.searchResults = []
    this.searchResultIndex = -1
    this.searchCount.textContent = 'Enter a search term'
    if (this.pdf && this.visiblePages.length) this.renderCurrentPage()
    this.stage.focus?.()
  },

  clearSearch() {
    this.searchRequestId += 1
    this.searchTextCache.clear()
    this.searchResults = []
    this.searchResultIndex = -1
    if (this.searchInput) this.searchInput.value = ''
    if (this.searchCount) this.searchCount.textContent = 'Enter a search term'
    if (this.searchBar) this.searchBar.hidden = true
  },

  async searchableItemsForPage(pageState) {
    const cacheKey = `${pageState.documentId}:${pageState.sourceIndex}`
    let items = this.searchTextCache.get(cacheKey)
    if (!items) {
      const page = await this.pdfForPage(pageState).getPage(pageState.sourceIndex + 1)
      const content = await this.getTextContentCompat(page)
      items = content.items.map((item, textIndex) => ({ textIndex, text: item.str || '' }))
      this.searchTextCache.set(cacheKey, items)
    }
    return items.map(item => ({
      ...item,
      text: pageState.textEdits[textItemId({ pageId: pageState.id, sourceIndex: pageState.sourceIndex, textIndex: item.textIndex })]?.text ?? item.text,
    }))
  },

  async updateSearch() {
    const requestId = ++this.searchRequestId
    const query = this.searchInput.value.trim().toLocaleLowerCase()
    this.searchResults = []
    this.searchResultIndex = -1
    if (!query) {
      this.searchCount.textContent = 'Enter a search term'
      if (this.pdf) await this.renderCurrentPage()
      return
    }

    this.searchCount.textContent = 'Searching…'
    const results = []
    for (const pageState of this.visiblePages) {
      const items = await this.searchableItemsForPage(pageState)
      if (requestId !== this.searchRequestId) return
      for (const item of items) {
        const count = occurrenceCount(item.text.toLocaleLowerCase(), query)
        for (let occurrence = 0; occurrence < count; occurrence += 1) {
          results.push({ pageId: pageState.id, textIndex: item.textIndex, occurrence })
        }
      }
    }
    if (requestId !== this.searchRequestId) return
    this.searchResults = results
    this.searchResultIndex = results.length ? 0 : -1
    this.updateSearchCount()
    if (results.length) await this.goToSearchResult(0)
    else {
      await this.renderCurrentPage()
      this.setStatus('No matches found. This PDF may not contain searchable text.')
    }
  },

  stepSearch(direction) {
    if (!this.searchResults.length) return
    const next = (this.searchResultIndex + direction + this.searchResults.length) % this.searchResults.length
    this.goToSearchResult(next)
  },

  async goToSearchResult(index) {
    const result = this.searchResults[index]
    if (!result) return
    this.searchResultIndex = index
    const pageIndex = this.visiblePages.findIndex(page => page.id === result.pageId)
    if (pageIndex >= 0) this.currentIndex = pageIndex
    this.updateSearchCount()
    await this.renderCurrentPage()
    const control = this.textEditLayer.querySelector('.search-current')
    control?.scrollIntoView({ block: 'center', inline: 'center' })
  },

  updateSearchCount() {
    this.searchCount.textContent = this.searchResults.length
      ? `${this.searchResultIndex + 1} of ${this.searchResults.length}`
      : 'No matches'
  },
}
